// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import * as adminService from '../../services/adminService';
import { getHospital } from '../../services/hospitalService';
import { getPatientProgress } from '../../services/patientService';
import { decryptDisplayName } from '../../shared/utils/encryptionUtils';

function parseErrorTrace(rawValue) {
  const rawText = String(rawValue || '').trim();

  if (!rawText) {
    return {
      formattedTrace: 'No error details available.',
      errorType: '',
      errorMessage: '',
    };
  }

  const fileMatches = [...rawText.matchAll(/File\s+"([^"]+)",\s+line\s+(\d+),\s+in\s+([^\s]+)\s*(.*?)(?=\s+File\s+"|\s+[A-Za-z_][\w.]*(?:Error|Exception|Fault):|$)/g)];
  const errorMatch = rawText.match(/([A-Za-z_][\w.]*(?:Error|Exception|Fault)):\s*(.+)$/);
  const formattedSections = [];

  if (rawText.startsWith('Traceback (most recent call last):')) {
    formattedSections.push('Traceback (most recent call last):');
  }

  fileMatches.forEach((match) => {
    const [, filePath, lineNumber, functionName, snippet] = match;
    const cleanedSnippet = String(snippet || '').trim();

    formattedSections.push(`  File "${filePath}", line ${lineNumber}, in ${functionName}`);
    if (cleanedSnippet) {
      formattedSections.push(`    ${cleanedSnippet}`);
    }
  });

  if (errorMatch) {
    formattedSections.push(`${errorMatch[1]}: ${errorMatch[2]}`);
  } else if (!fileMatches.length) {
    formattedSections.push(rawText);
  }

  return {
    formattedTrace: formattedSections.join('\n'),
    errorType: errorMatch?.[1] || '',
    errorMessage: errorMatch?.[2] || '',
  };
}

function DetailField({ label, value }) {
  return (
    <TextField
      margin="dense"
      label={label}
      value={value || 'N/A'}
      fullWidth
      InputProps={{ readOnly: true }}
      sx={{
        '& .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled': {
          color: 'rgba(0,0,0,0.87)',
          WebkitTextFillColor: 'rgba(0,0,0,0.87)',
        },
        '& .MuiInputBase-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(0,0,0,0.23)',
        },
        '& .MuiInputBase-root': {
          cursor: 'default',
        },
        '& .MuiInputBase-input': {
          cursor: 'default',
        },
      }}
    />
  );
}

function CodeBlockContainer({ value }) {
  const lines = String(value || '').split('\n');

  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 0,
        fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
        fontSize: '13px',
        lineHeight: '1.45',
        borderRadius: '8px',
        padding: '16px',
        background: '#fafafa',
      }}
    >
      {lines.map((line, index) => (
        <Box
          key={`${index}-${line}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: '48px minmax(0, 1fr)',
            px: 2,
            py: 0.125,
            bgcolor: index % 2 === 0 ? 'transparent' : 'rgba(15, 23, 42, 0.02)',
          }}
        >
          <Box component="span" sx={{ color: 'text.disabled', userSelect: 'none', pr: 2, textAlign: 'right' }}>
            {index + 1}
          </Box>
          <Box component="code" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.primary' }}>
            {line || ' '}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function ErrorViewDialog({ open, onClose, errorRecord, loading = false }) {
  const [expandedPanel, setExpandedPanel] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyResetTimeoutRef = useRef(null);
  const [userDetails, setUserDetails] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState('');
  const [patientDetails, setPatientDetails] = useState(null);
  const [patientLoading, setPatientLoading] = useState(false);
  const [patientError, setPatientError] = useState('');
  const [hospitalDetails, setHospitalDetails] = useState(null);
  const [hospitalLoading, setHospitalLoading] = useState(false);
  const [hospitalError, setHospitalError] = useState('');

  const parsedTrace = useMemo(() => parseErrorTrace(errorRecord?.error), [errorRecord?.error]);
  const patientHospitalId = patientDetails?.hospitalId || '';
  const showUserAccordion = Boolean(errorRecord?.userId);
  const showPatientAccordion = Boolean(errorRecord?.patientId);

  useEffect(() => {
    if (!open) {
      setExpandedPanel(false);
      setCopied(false);
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current);
        copyResetTimeoutRef.current = null;
      }
      setUserDetails(null);
      setUserLoading(false);
      setUserError('');
      setPatientDetails(null);
      setPatientLoading(false);
      setPatientError('');
      setHospitalDetails(null);
      setHospitalLoading(false);
      setHospitalError('');
    }
  }, [open]);

  const loadUserDetails = async () => {
    if (!errorRecord?.userId || userLoading || userDetails) {
      return;
    }

    setUserLoading(true);
    setUserError('');

    try {
      const user = await adminService.getUser(errorRecord.userId);
      setUserDetails({
        uid: user?.uid || errorRecord.userId,
        name: [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.displayName || '',
        email: user?.email || '',
        hospitalId: user?.hospitalId || '',
      });
    } catch (err) {
      console.error('loadUserDetails failed', err);
      setUserError(err?.message || 'Failed to load user details');
    } finally {
      setUserLoading(false);
    }
  };

  const loadPatientDetails = async () => {
    if (!errorRecord?.patientId || patientLoading || patientDetails) {
      return;
    }

    setPatientLoading(true);
    setPatientError('');
    setHospitalError('');

    try {
      const patient = await getPatientProgress(errorRecord.patientId);
      const hospitalId = patient?.hospital_id || '';
      const rawName = patient?.display_name || '';
      let decryptedName = rawName;
      if (rawName) {
        try {
          decryptedName = await decryptDisplayName(errorRecord.patientId, rawName);
        } catch {
          decryptedName = '[name unavailable]';
        }
      }

      setPatientDetails({
        patientId: patient?.patient_id || errorRecord.patientId,
        name: decryptedName,
        userId: patient?.user_id || '',
        hospitalId,
        step: patient?.step || '',
      });

      if (hospitalId) {
        setHospitalLoading(true);
        try {
          const hospital = await getHospital(hospitalId);
          setHospitalDetails({
            id: hospital?.id || hospitalId,
            name: hospital?.name || '',
          });
        } catch (err) {
          console.error('loadHospitalDetails failed', err);
          setHospitalError(err?.message || 'Failed to load hospital details');
        } finally {
          setHospitalLoading(false);
        }
      }
    } catch (err) {
      console.error('loadPatientDetails failed', err);
      setPatientError('Failed to load patient details.');
    } finally {
      setPatientLoading(false);
    }
  };

  const handleAccordionChange = (panel) => async (_, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);

    if (!isExpanded) {
      return;
    }

    if (panel === 'user') {
      await loadUserDetails();
      return;
    }

    if (panel === 'patient') {
      await loadPatientDetails();
      return;
    }
  };

  const handleCopyTrace = async () => {
    if (!parsedTrace.formattedTrace || !navigator?.clipboard?.writeText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(parsedTrace.formattedTrace);
      setCopied(true);
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = window.setTimeout(() => {
        setCopied(false);
        copyResetTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      console.error('copy trace failed', err);
    }
  };

  if (!errorRecord) return null;

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="lg" fullWidth disableScrollLock>
      <DialogTitle sx={{ pb: 1 }}>Error Details</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {errorRecord.api ? <Chip label={errorRecord.api} color="error" variant="outlined" /> : null}
              {parsedTrace.errorType ? <Chip label={parsedTrace.errorType} color="warning" variant="filled" /> : null}
            </Stack>
            {parsedTrace.errorMessage ? (
              <Typography variant="body2" color="text.primary" sx={{ mb: 0.75 }}>
                {parsedTrace.errorMessage}
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Document ID:
              </Box>{' '}
              {errorRecord.id || 'N/A'}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '2.4fr 1fr' },
              gap: 2,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                overflow: 'hidden',
                borderRadius: 2,
                borderColor: 'divider',
                backgroundColor: '#fafafa',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1.25,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: '#f3f4f6',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Stacktrace
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={handleCopyTrace}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </Box>
              <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
                <CodeBlockContainer value={parsedTrace.formattedTrace} />
              </Box>
            </Paper>

            <Stack spacing={2}>
              {showPatientAccordion ? (
                <Accordion expanded={expandedPanel === 'patient'} onChange={handleAccordionChange('patient')} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Patient Info</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {patientError ? <Alert severity="error" sx={{ mb: 1.5 }}>{patientError}</Alert> : null}
                    {patientLoading ? (
                      <Stack spacing={1.25}>
                        <Skeleton variant="rounded" height={56} />
                        <Skeleton variant="rounded" height={56} />
                        <Skeleton variant="rounded" height={56} />
                      </Stack>
                    ) : (
                      <Stack spacing={1.5}>
                        <DetailField label="Patient ID" value={patientDetails?.patientId || errorRecord.patientId} />
                        <DetailField label="Name" value={patientDetails?.name} />
                        <DetailField label="Pipeline Step" value={patientDetails?.step} />
                        {patientHospitalId && hospitalLoading ? (
                          <>
                            <Skeleton variant="rounded" height={56} />
                            <Skeleton variant="rounded" height={56} />
                          </>
                        ) : (
                          <>
                            <DetailField label="Hospital ID" value={patientHospitalId || undefined} />
                            <DetailField label="Hospital Name" value={hospitalDetails?.name || undefined} />
                          </>
                        )}
                      </Stack>
                    )}
                  </AccordionDetails>
                </Accordion>
              ) : null}

              {showUserAccordion ? (
                <Accordion expanded={expandedPanel === 'user'} onChange={handleAccordionChange('user')} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>User Info</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {userError ? <Alert severity="error" sx={{ mb: 1.5 }}>{userError}</Alert> : null}
                    {userLoading ? (
                      <Stack spacing={1.25}>
                        <Skeleton variant="rounded" height={56} />
                        <Skeleton variant="rounded" height={56} />
                        <Skeleton variant="rounded" height={56} />
                      </Stack>
                    ) : (
                      <Stack spacing={1.5}>
                        <DetailField label="User ID" value={userDetails?.uid || errorRecord.userId} />
                        <DetailField label="Name" value={userDetails?.name} />
                        <DetailField label="Email" value={userDetails?.email} />
                      </Stack>
                    )}
                  </AccordionDetails>
                </Accordion>
              ) : null}

            </Stack>
          </Box>

          {loading ? <Divider /> : null}
          {loading ? <Typography variant="body2" color="text.secondary">Loading details...</Typography> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}